import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ClothingSelection from './ClothingSelection';
import TemplateSelection from './TemplateSelection';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import SubscriptionGuard from '../ui/SubscriptionGuard';
import CustomSelect from '../ui/CustomSelect';
import { getSubscriptionStatus, SubscriptionType, isPlusPlan, isProPlan, isEnterprisePlan } from '../../services/subscriptionService';

interface RealisticSettings {
  variations: number;
  ethnicity: string;
  gender: string;
  prompt: string;
}

const RealisticPhotoshoot: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  // State
  const [clothingImageUrls, setClothingImageUrls] = useState<string[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Array<{ id: string, url: string, prompt: string }>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [generationTime, setGenerationTime] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [settings, setSettings] = useState<RealisticSettings>({
    variations: 1,
    ethnicity: 'auto',
    gender: 'female',
    prompt: ''
  });
  const [mode, setMode] = useState<string>('performance');
  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showSubscriptionGuard, setShowSubscriptionGuard] = useState(false);
  const [hasVideoAccess, setHasVideoAccess] = useState<boolean>(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('realistic-tryon');

  // Mode restrictions hook
  const modeRestrictions = useModeRestrictions();

  // Load state from gallery navigation
  useEffect(() => {
    const state = location.state as any;
    if (state) {
      console.log('Loading from gallery:', state);

      // Set input images (clothing)
      if (state.inputImages && Array.isArray(state.inputImages)) {
        setClothingImageUrls(state.inputImages);
      }

      // Set generated images to display results
      if (state.generatedImages && Array.isArray(state.generatedImages)) {
        setGeneratedImages(state.generatedImages);
      }

      // Set prompt if available
      if (state.prompt) {
        setSettings(prev => ({ ...prev, prompt: state.prompt }));
        setShowAdvancedSettings(true); // Show advanced settings if prompt exists
      }

      // Clear location state after loading to prevent re-loading on re-renders
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  // Check subscription status for video generation access
  useEffect(() => {
    const checkVideoAccess = async () => {
      if (!currentUser?.uid) {
        setHasVideoAccess(false);
        return;
      }

      try {
        const response = await getSubscriptionStatus(currentUser.uid);
        const hasAccess = isProPlan(response.type) || isEnterprisePlan(response.type);
        setHasVideoAccess(hasAccess);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setHasVideoAccess(false);
      }
    };

    checkVideoAccess();
  }, [currentUser]);

  // Handle mode restrictions
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
      const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
      if (availableMode) {
        setMode(availableMode.value);
      }
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode]);

  // Credit estimation effect
  useEffect(() => {
    if (!currentUser?.uid || clothingImageUrls.length === 0 || selectedTemplateIds.length === 0) {
      return;
    }

    const estimateCost = async () => {
      // Filter out 'random' template and build template array
      const actualTemplates = selectedTemplates.filter(t => t.id !== 'random');
      const templateArray = (actualTemplates.length > 0)
        ? actualTemplates.map(t => ({
          templateId: t.id,
          templateUrl: t.url,
          prompt: t.prompt || ''
        }))
        : (selectedTemplateIds.includes('random')
          ? [{ templateId: '-1', templateUrl: '', prompt: '' }]
          : []);

      const estimationPayload = {
        template: templateArray,
        clothing: {
          id: "clothing_estimation",
          imageUrl: [clothingImageUrls[0]] // Array with first clothing image
        },
        clothType: "auto",
        prompt: settings.prompt || "",
        numImages: 1, // Number of variations per template (fixed to 1)
        ethnicity: settings.ethnicity,
        gender: settings.gender,
        mode: mode.toLowerCase(),
        userId: currentUser?.uid || "user_id",
        quality: "high",
        aspectRatio: "9:16",
        enableSafetyChecker: true,
        outputFormat: "jpeg",
        numInferenceSteps: 28,
        guidanceScale: 3.5
      };

      estimateCreditCost(estimationPayload);
    };

    estimateCost();
  }, [
    currentUser?.uid,
    clothingImageUrls,
    selectedTemplateIds,
    selectedTemplates,
    settings.ethnicity,
    settings.prompt,
    mode,
    estimateCreditCost
  ]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  const startTimer = () => {
    setGenerationTime(0);
    const interval = setInterval(() => {
      setGenerationTime(prev => prev + 0.1);
    }, 100);
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const handleGenerate = async () => {
    if (clothingImageUrls.length === 0) {
      setErrorMessage('Please upload a clothing image');
      return;
    }

    if (selectedTemplateIds.length === 0) {
      setErrorMessage('Please select at least one template');
      return;
    }

    if (!currentUser) {
      alert('Please log in to generate content');
      return;
    }

    // Check if user can afford the generation
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    // Check subscription status for Realistic Photoshoot - requires Plus, Pro, or Enterprise plan
    try {
      const subscriptionData = await getSubscriptionStatus(currentUser.uid);

      // Check if user has Plus, Pro or Enterprise access (not Free)
      if (!isPlusPlan(subscriptionData.type) && !isProPlan(subscriptionData.type) && !isEnterprisePlan(subscriptionData.type)) {
        setShowSubscriptionGuard(true);
        return;
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      alert('Unable to verify subscription status. Please try again.');
      return;
    }

    setIsProcessing(true);
    setGeneratedImages([]);
    setErrorMessage('');
    startTimer();

    try {
      // Filter out 'random' template and get actual templates with all data
      const actualTemplates = selectedTemplates.filter(t => t.id !== 'random');

      // Build template array with templateId, templateUrl, and prompt
      const templateArray = (actualTemplates.length > 0)
        ? actualTemplates.map(t => ({
          templateId: t.id,
          templateUrl: t.url,
          prompt: t.prompt || ''
        }))
        : (selectedTemplateIds.includes('random')
          ? [{ templateId: '-1', templateUrl: '', prompt: '' }]
          : []);

      const payload: any = {
        template: templateArray,
        clothing: {
          id: "clothing_" + Date.now(),
          imageUrl: [clothingImageUrls[0]] // Array with first clothing image
        },
        clothType: "auto",
        prompt: settings.prompt || "",
        numImages: 1, // Number of variations per template (fixed to 1)
        ethnicity: settings.ethnicity,
        gender: settings.gender,
        mode: mode.toLowerCase(),
        userId: currentUser?.uid || "user_id",
        quality: "high",
        aspectRatio: "9:16",
        enableSafetyChecker: true,
        outputFormat: "jpeg",
        numInferenceSteps: 28,
        guidanceScale: 3.5
      };

      console.log('Realistic Tryon Payload:', payload);

      const response = await fetch(API_CONFIG.REALISTIC_TRYON_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Check HTTP status
      if (!response.ok) {
        setErrorMessage('Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      // Check if images were returned
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
        await refreshUserCredits();
        setErrorMessage('');
        // Clear selected templates after successful generation
        setSelectedTemplateIds([]);
        setSelectedTemplates([]);
      } else {
        setErrorMessage(data.message || 'Failed to generate images. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate Realistic photoshoot:', error);
      setErrorMessage('Failed to generate images. Please try again.');
    } finally {
      stopTimer();
      setIsProcessing(false);
    }
  };

  const handleClothingExampleSelect = (clothingUrl: string, clothingId: number) => {
    // Handle example selection if needed
  };

  const handleTemplateSelect = (templateUrl: string, templateId: string, templatePrompt?: string) => {
    console.log('Template selected:', templateId, templateUrl, templatePrompt);
    // Update selected templates with ID, URL, and prompt
    setSelectedTemplates(prev => {
      const exists = prev.find(t => t.id === templateId);
      if (exists) {
        // Remove if already exists (deselection)
        return prev.filter(t => t.id !== templateId);
      } else {
        // Add new template
        return [...prev, { id: templateId, url: templateUrl, prompt: templatePrompt || '' }];
      }
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3">
      {/* 4-Column Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Column 1: Clothing Upload - 1 column */}
        <div className="lg:col-span-1">
          <ClothingSelection
            imageUrls={clothingImageUrls}
            setImageUrls={setClothingImageUrls}
            isExample={false}
            onExampleSelect={handleClothingExampleSelect}
          />
        </div>

        {/* Column 2-3: Template Selector - 2 columns */}
        <div className="lg:col-span-2">
          <TemplateSelection
            selectedTemplateIds={selectedTemplateIds}
            setSelectedTemplateIds={setSelectedTemplateIds}
            onTemplateSelect={handleTemplateSelect}
            selectedGender={settings.gender}
            setSelectedGender={(gender) => setSettings({ ...settings, gender })}
          />
        </div>

        {/* Column 4: Settings Panel - 1 column */}
        <div className="lg:col-span-1 space-y-3">
          {/* Mode Selection */}
          <div className="bg-accent-sand rounded-lg p-3">
            <label className="block text-sm font-medium text-text-heading mb-2">
              Mode
              {!modeRestrictions.hasInitialLoad && (
                <span className="ml-2 text-xs text-text-description">(Loading...)</span>
              )}
            </label>
            <div className="relative">
              <CustomSelect
                value={mode}
                onChange={(value) => setMode(value)}
                options={modeRestrictions.availableModes.map((modeOption) => {
                  let planRequirement = '';
                  if (modeOption.disabled && modeOption.requiresPlan) {
                    const minPlan = modeOption.requiresPlan[0];
                    switch (minPlan) {
                      case 'plus':
                        planRequirement = ' (Plus Plan Required)';
                        break;
                      case 'pro':
                        planRequirement = ' (Pro Plan Required)';
                        break;
                      case 'enterprise':
                        planRequirement = ' (Enterprise Plan Required)';
                        break;
                      default:
                        planRequirement = ' (Upgrade Required)';
                    }
                  }
                  return {
                    value: modeOption.value,
                    label: `${modeOption.label}${planRequirement}`,
                    disabled: modeOption.disabled
                  };
                })}
                disabled={!modeRestrictions.hasInitialLoad}
                className="w-full"
              />
              {modeRestrictions.error && (
                <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
              )}
              {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
                <p className="text-xs text-orange-600 mt-1">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  {modeRestrictions.getRestrictedModeMessage(mode)}
                </p>
              )}
            </div>
          </div>

          {/* Ethnicity */}
          <div className="bg-accent-sand rounded-lg p-3">
            <label className="block text-sm font-medium text-text-heading mb-2">Ethnicity</label>
            <CustomSelect
              value={settings.ethnicity}
              onChange={(value) => setSettings({ ...settings, ethnicity: value })}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'indian', label: 'Indian' },
                { value: 'american', label: 'American' },
                { value: 'latino', label: 'Latino' },
                { value: 'asian', label: 'Asian' },
                { value: 'african', label: 'African' },
                { value: 'european', label: 'European' }
              ]}
              className="w-full"
            />
          </div>

          {/* Advanced Settings */}
          <div className="bg-accent-sand rounded-lg p-3">
            <button
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              className="w-full flex items-center justify-between text-sm font-medium text-text-heading hover:text-secondary-600 transition-colors"
            >
              <span className="flex items-center">
                <i className="fas fa-sliders-h mr-2"></i>
                Advanced Settings
              </span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${showAdvancedSettings ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showAdvancedSettings && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-text-heading mb-1">Prompt</label>
                <textarea
                  value={settings.prompt}
                  onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                  placeholder="Add any specific instructions for the photoshoot (e.g., brazilian woman wearing this dress in a sunlit tropical garden, radiating confidence and effortless elegance)"
                  className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 min-h-[80px] resize-y"
                />
              </div>
            )}
          </div>

          {/* Credit Cost Preview */}
          <div className="bg-accent-sand rounded-lg p-2">
            <CreditCostPreview
              creditCost={creditEstimation.creditCost}
              isEstimating={creditEstimation.isEstimating}
              error={creditEstimation.error}
              canAfford={creditEstimation.canAfford}
            />
          </div>

          {/* Clear All Settings Button */}
          <button
            onClick={() => {
              setClothingImageUrls([]);
              setSelectedTemplateIds([]);
              setSelectedTemplates([]);
              setSettings({ variations: 1, ethnicity: 'auto', gender: 'female', prompt: '' });
              setMode('performance');
              setGeneratedImages([]);
              setErrorMessage('');
              setShowAdvancedSettings(false);
            }}
            className="w-full py-2.5 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
            title="Clear all settings and images"
          >
            Clear All Settings
          </button>

          {/* Generate Button */}
          <button
            className={`w-full py-3 rounded-lg font-medium transition-colors ${clothingImageUrls.length > 0 && selectedTemplateIds.length > 0
                ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                : 'bg-gray-300 text-text-description cursor-not-allowed'
              }`}
            onClick={handleGenerate}
            disabled={clothingImageUrls.length === 0 || selectedTemplateIds.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center space-x-3">
                <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>Generating...</span>
                <div className="flex items-center space-x-1 opacity-90">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-mono font-semibold text-sm">
                    {generationTime.toFixed(1)}s
                  </span>
                </div>
              </div>
            ) : (
              <>
                <i className="fas fa-camera mr-2"></i>
                Generate Photoshoot
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Generated Results Section */}
      {generatedImages.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Generated Results</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {generatedImages.map((url, idx) => (
              <div
                key={idx}
                className="relative flex flex-col h-80 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 group overflow-hidden border border-accent-taupe"
              >
                {/* Overlay index badge */}
                <div className="absolute top-2 left-2 z-10 bg-secondary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow">
                  #{idx + 1}
                </div>
                {/* Image area */}
                <div className="flex-1 flex items-center justify-center bg-accent-sand">
                  <img
                    src={url}
                    alt={`Generated ${idx + 1}`}
                    className="max-h-60 max-w-full object-contain drop-shadow rounded-xl transition-transform duration-300 group-hover:scale-105 bg-white"
                  />
                </div>
                {/* Action bar */}
                <div className="flex justify-center items-center gap-2 px-4 py-2 bg-white rounded-b-2xl shadow-md mt-2">
                  {/* Download Button */}
                  <button
                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                    onClick={async () => {
                      try {
                        const response = await fetch(url);
                        const blob = await response.blob();
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = `realistic-result-${idx + 1}.jpg`;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                      } catch (error) {
                        console.error('Download failed:', error);
                      }
                    }}
                  >
                    Download
                  </button>
                  {/* Generate Video Button */}
                  <button
                    className={`relative px-3 py-1.5 rounded-lg transition-colors text-sm font-medium border ${hasVideoAccess
                      ? 'text-text-description hover:text-secondary-600 hover:bg-secondary-100 border-accent-taupe hover:border-secondary-300'
                      : 'text-text-description cursor-not-allowed bg-accent-sand border-accent-taupe'
                      }`}
                    onClick={() => {
                      if (hasVideoAccess) {
                        navigate('/canvas-editor', {
                          state: {
                            inputImages: [url],
                            prompt: '',
                            style: '',
                            variations: 1,
                            recreatingFrom: '',
                          }
                        });
                      } else {
                        navigate('/subscription');
                      }
                    }}
                  >
                    Create Video
                    {!hasVideoAccess && (
                      <div className="status-dot-top-right">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                  {/* Upscale Button */}
                  <button
                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                    onClick={() => {
                      navigate('/canvas-editor', {
                        state: {
                          inputImages: [url],
                          prompt: '',
                          style: '',
                          variations: 1,
                          recreatingFrom: '',
                          tool: 'upscaler'
                        }
                      });
                    }}
                  >
                    HD
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Realistic Photoshoot"
      />

      {/* Subscription Guard Modal */}
      {showSubscriptionGuard && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSubscriptionGuard(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSubscriptionGuard(false)}
              className="absolute top-4 right-4 z-10 bg-accent-sand hover:bg-accent-taupe text-text-description rounded-full p-2 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <SubscriptionGuard
              requiredPlans={[SubscriptionType.PLUS, SubscriptionType.PRO, SubscriptionType.ENTERPRISE]}
              featureName="Realistic Photoshoot"
            >
              <></>
            </SubscriptionGuard>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealisticPhotoshoot;
